/** ユーザーの追加(Modal)。Keycloak の形に組み替えるのは API の仕事。 */
import { useMutation } from "@tanstack/react-query";
import { Flex, Form, Input, Modal, Radio, Select, Switch, message } from "antd";
import { useEffect, useState } from "react";

import type { IdpUserForm } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { errorText, useInvalidateIdp, useRoleOptions } from "./hooks";
import { AttrFields, type UserValues } from "./userDialogs";
import { tellPassword } from "./userPassword";

export function AddUserModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const api = useApi();
  const roleOpts = useRoleOptions();
  const invalidate = useInvalidateIdp();
  const [form] = Form.useForm<UserValues>();
  const [generate, setGenerate] = useState(true);

  useEffect(() => {
    if (open) {
      form.resetFields();
      setGenerate(true);
    }
  }, [open, form]);

  const create = useMutation({
    mutationFn: (v: UserValues) => {
      const body: IdpUserForm = {
        username: v.username,
        email: v.email,
        firstName: v.firstName,
        lastName: v.lastName,
        enabled: v.enabled ?? true,
        roles: v.roles ?? [],
        attributes: v.attrs ?? {},
        generate: v.generate ?? true,
        password: v.password ?? "",
        temporary: v.temporary ?? true,
      };
      return api.createIdpUser(body);
    },
    onSuccess: (res) => {
      invalidate();
      tellPassword(res);
      onClose();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  return (
    <Modal
      open={open}
      title="ユーザーの追加"
      width={640}
      onCancel={onClose}
      onOk={() => void form.submit()}
      okText="追加する"
      cancelText="やめる"
      confirmLoading={create.isPending}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ enabled: true, generate: true, temporary: true, roles: [] }}
        onFinish={(v) => create.mutate(v)}
      >
        <Form.Item
          name="username"
          label="ユーザー名(ログインID)"
          rules={[{ required: true, message: "ユーザー名は必須" }]}
        >
          <Input maxLength={255} placeholder="taro.yamada" />
        </Form.Item>
        <Form.Item name="email" label="メールアドレス">
          <Input maxLength={255} placeholder="taro@example.jp" />
        </Form.Item>
        <Flex gap={12}>
          <Form.Item name="lastName" label="姓" style={{ flex: 1 }}>
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item name="firstName" label="名" style={{ flex: 1 }}>
            <Input maxLength={100} />
          </Form.Item>
        </Flex>
        <Form.Item name="roles" label="権限(スイートのロール)">
          <Select mode="multiple" allowClear options={roleOpts} placeholder="付けるロール" />
        </Form.Item>
        <AttrFields />
        <Form.Item name="enabled" label="状態" valuePropName="checked">
          <Switch checkedChildren="有効" unCheckedChildren="無効" />
        </Form.Item>
        <Form.Item name="generate" label="初期パスワード">
          <Radio.Group onChange={(e) => setGenerate(Boolean(e.target.value))}>
            <Radio value={true}>自動で作る(推奨)</Radio>
            <Radio value={false}>自分で決める</Radio>
          </Radio.Group>
        </Form.Item>
        {!generate && (
          <Form.Item
            name="password"
            label="初期パスワード"
            rules={[{ required: true, message: "初期パスワードを入れる" }]}
          >
            <Input.Password maxLength={200} />
          </Form.Item>
        )}
        <Form.Item
          name="temporary"
          label="最初のログインで変更させる"
          valuePropName="checked"
          extra="メールが送れるときは初期パスワードをメールで送り、画面には出さない。"
        >
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
}
